import { api } from './api'

export const getProfile = async () => {
  const { data } = await api.get('/profile-msp')

  if (!data) {
    throw new Error('No profile data received')
  }

  return mapProfileResponse(data)
}

const mapProfileResponse = (profile) => ({
  firstName: profile?.name?.first ?? profile?.firstName ?? '',
  middleName: profile?.name?.middle ?? profile?.middleName ?? '',
  lastName: profile?.name?.last ?? profile?.lastName ?? '',
  preferredName: profile?.name?.preferred ?? profile?.preferredName ?? '',
  // TIDC uses `honorificSuffix`; keep `suffix` in the client shape for the form and display.
  suffix: profile?.honorificSuffix ?? profile?.suffix ?? '',
  email: profile?.email ?? '',
  phone: profile?.primaryPhoneNumber ?? profile?.phone ?? '',
  dateOfBirth: profile?.dateOfBirth ?? '',
  addressLine1: profile?.primaryAddress?.address1 ?? profile?.addressLine1 ?? '',
  addressCity: profile?.primaryAddress?.city ?? profile?.addressCity ?? '',
  addressState: profile?.primaryAddress?.state ?? profile?.addressState ?? '',
  addressZipCode: profile?.primaryAddress?.zipCode ?? profile?.addressZipCode ?? '',
  addressCountry: profile?.primaryAddress?.country ?? profile?.addressCountry ?? '',
  altAddressLine1: profile?.mailingAddress?.address1 ?? profile?.altAddressLine1 ?? '',
  altAddressCity: profile?.mailingAddress?.city ?? profile?.altAddressCity ?? '',
  altAddressState: profile?.mailingAddress?.state ?? profile?.altAddressState ?? '',
  altAddressZipCode: profile?.mailingAddress?.zipCode ?? profile?.altAddressZipCode ?? '',
  altAddressCountry: profile?.mailingAddress?.country ?? profile?.altAddressCountry ?? '',
  passwordChanged: profile?.passwordChanged ?? '',
  factorTOTPName: profile?.factorTOTPName ?? '',
  factorGoogleAuth: profile?.factorGoogleAuth ?? false,
  factorTwilio: profile?.factorTwilio ?? false,
  factorLastpass: profile?.factorLastpass ?? false,
})

const normalizePhone = (phone) => {
  if (!phone) return ''
  const digits = phone.replace(/[^\d]/g, '')

  if (digits.length === 10) {
    return `+1${digits}`
  }

  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`
  }

  // Fallback: if the user included a plus, keep it with cleaned digits; otherwise return cleaned digits
  return phone.startsWith('+') ? `+${digits}` : digits
}

const buildProfilePayload = (profile) => {
  const hasPrimaryAddress =
    profile.addressLine1 || profile.addressCity ||
    profile.addressState || profile.addressZipCode

  const hasMailingAddress =
    profile.altAddressLine1 || profile.altAddressCity ||
    profile.altAddressState || profile.altAddressZipCode

  return {
    name: {
      first: profile.firstName || '',
      middle: profile.middleName || '',
      last: profile.lastName || '',
      preferred: profile.preferredName || '',
      suffix: profile.suffix ?? profile.honorificSuffix ?? '',
    },
    email: profile.email || '',
    primaryPhoneNumber: normalizePhone(profile.phone),
    ...(hasPrimaryAddress && {
      primaryAddress: {
        address1: profile.addressLine1 || '',
        city: profile.addressCity || '',
        state: profile.addressState || '',
        zipCode: profile.addressZipCode || '',
      },
    }),
    ...(hasMailingAddress && {
      mailingAddress: {
        address1: profile.altAddressLine1 || '',
        city: profile.altAddressCity || '',
        state: profile.altAddressState || '',
        zipCode: profile.altAddressZipCode || '',
      },
    }),
  }
}

export const updateProfile = async (profile) => {
  const { data } = await api.put('/profile-msp', buildProfilePayload(profile))
  if (!data) {
    throw new Error('No profile data received')
  }

  return mapProfileResponse(data)
}
